import { useState, useEffect, useRef } from 'react';
import { useChat } from '@/contexts/ChatContext';
import { X, Send, Sparkles, MessageCircle, Headphones, Check, ArrowRight, Phone, Loader2, RefreshCw, Mic, MicOff, Volume2, VolumeX } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTED_PROMPTS = [
  'How does the first-month-free premium work?',
  'Show me how to list my property for sale',
  'What home services do you offer?',
  'I want to talk to a human',
];

const newSessionId = () =>
  (window.crypto?.randomUUID?.() || `s_${Math.random().toString(36).slice(2)}_${Date.now()}`);

const WELCOME = {
  role: 'assistant',
  text: "Namaste! I'm Bharat AI — your concierge for everything on Bharat Estate. Ask me about properties, premium membership, home services, calculators, or anything else. How can I help today?",
};

const BharatAIChat = () => {
  const { user } = useAuth();
  const { open, setOpen } = useChat();
  const [visible, setVisible] = useState(false);
  const [sessionId] = useState(newSessionId);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [showTicketForm, setShowTicketForm] = useState(false);
  const [ticketForm, setTicketForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [ticketSubmitting, setTicketSubmitting] = useState(false);
  const [ticketCreated, setTicketCreated] = useState(null);
  const [unread, setUnread] = useState(false);

  // Voice state
  const SpeechRec = typeof window !== 'undefined'
    ? (window.SpeechRecognition || window.webkitSpeechRecognition)
    : null;
  const ttsSupported = typeof window !== 'undefined' && 'speechSynthesis' in window;
  const voiceSupported = !!SpeechRec;
  const [listening, setListening] = useState(false);
  const [interimText, setInterimText] = useState('');
  const [autoSpeak, setAutoSpeak] = useState(false);
  const recognitionRef = useRef(null);

  const prefilledRef = useRef(false);
  const scrollerRef = useRef(null);
  const inputRef = useRef(null);

  // Slight reveal delay so it doesn't fight the popup
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 1500);
    return () => clearTimeout(t);
  }, []);

  // Auto-fill ticket form from logged-in user (once)
  useEffect(() => {
    if (!user || prefilledRef.current) return;
    prefilledRef.current = true;
    setTicketForm((f) => ({
      ...f,
      name: f.name || user.name || '',
      email: f.email || user.email || '',
      phone: f.phone || user.phone || '',
    }));
  }, [user]);

  // Auto-scroll to latest message
  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, sending, showTicketForm, ticketCreated]);

  // Focus input when opened
  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
    if (open) setUnread(false);
  }, [open]);

  // ===== Voice helpers =====
  const speak = (text) => {
    if (!ttsSupported || !text) return;
    try {
      window.speechSynthesis.cancel();
      const utter = new window.SpeechSynthesisUtterance(text);
      utter.rate = 1.02;
      utter.pitch = 1;
      utter.lang = 'en-IN';
      // Prefer an Indian English voice if available
      const voices = window.speechSynthesis.getVoices();
      const pick =
        voices.find((v) => /en[-_]?IN/i.test(v.lang)) ||
        voices.find((v) => /female/i.test(v.name)) ||
        voices.find((v) => /en/i.test(v.lang));
      if (pick) utter.voice = pick;
      window.speechSynthesis.speak(utter);
    } catch { /* ignore */ }
  };

  const stopSpeaking = () => {
    if (ttsSupported) {
      try { window.speechSynthesis.cancel(); } catch { /* ignore */ }
    }
  };

  // Auto-speak the latest assistant reply when voice mode is on
  const lastSpokenIdxRef = useRef(-1);
  useEffect(() => {
    if (!autoSpeak || !open) return;
    const lastIdx = messages.length - 1;
    const last = messages[lastIdx];
    if (last && last.role === 'assistant' && lastIdx !== lastSpokenIdxRef.current) {
      lastSpokenIdxRef.current = lastIdx;
      speak(last.text);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, autoSpeak, open]);

  // Stop speaking when the panel closes
  useEffect(() => {
    if (!open) {
      stopSpeaking();
      stopListening();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const startListening = () => {
    if (!voiceSupported || listening || sending) return;
    try {
      stopSpeaking();
      const rec = new SpeechRec();
      rec.lang = 'en-IN';
      rec.interimResults = true;
      rec.continuous = false;
      rec.maxAlternatives = 1;

      rec.onstart = () => setListening(true);
      rec.onresult = (event) => {
        let interim = '';
        let final = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const t = event.results[i][0].transcript;
          if (event.results[i].isFinal) final += t;
          else interim += t;
        }
        setInterimText(interim);
        if (final) {
          setInput((prev) => (prev ? prev + ' ' : '') + final.trim());
        }
      };
      rec.onerror = (e) => {
        if (e && e.error && e.error !== 'no-speech' && e.error !== 'aborted') {
          toast.error('Mic error: ' + e.error);
        }
        setListening(false);
        setInterimText('');
      };
      rec.onend = () => {
        setListening(false);
        setInterimText('');
        // Auto-send if we captured something
        setInput((prev) => {
          const trimmed = (prev || '').trim();
          if (trimmed.length >= 2) {
            // small delay so React commits state first
            setTimeout(() => sendMessage(trimmed), 50);
            return '';
          }
          return prev;
        });
      };

      recognitionRef.current = rec;
      rec.start();
    } catch (err) {
      toast.error('Could not start voice input.');
      setListening(false);
    }
  };

  const stopListening = () => {
    try {
      recognitionRef.current?.stop();
    } catch { /* ignore */ }
    setListening(false);
    setInterimText('');
  };

  const toggleMic = () => {
    if (listening) stopListening();
    else startListening();
  };

  const sendMessage = async (rawText) => {
    const text = (rawText ?? input).trim();
    if (!text || sending) return;
    setMessages((m) => [...m, { role: 'user', text }]);
    setInput('');
    setSending(true);
    try {
      const res = await fetch(`${API}/chat/bharat-ai`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      const data = await res.json();
      const reply = data.reply || "I'm not sure how to answer that. Want me to create a support ticket?";
      setMessages((m) => [...m, { role: 'assistant', text: reply, suggestTicket: !!data.suggest_ticket }]);
      if (data.suggest_ticket) {
        setShowTicketForm(true);
        setTicketForm((f) => ({ ...f, subject: f.subject || text.slice(0, 80), message: f.message || text }));
      }
      if (!open) setUnread(true);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: 'assistant',
          text: "I'm having trouble connecting right now. Please try again or raise a quick support ticket.",
          suggestTicket: true,
        },
      ]);
      setShowTicketForm(true);
    } finally {
      setSending(false);
    }
  };

  const submitTicket = async () => {
    const { name, email, subject, message } = ticketForm;
    if (!name.trim() || !email.trim() || !subject.trim() || !message.trim()) {
      toast.error('Please fill name, email, subject and message.');
      return;
    }
    setTicketSubmitting(true);
    try {
      const res = await fetch(`${API}/support/tickets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...ticketForm, chat_session_id: sessionId }),
      });
      if (!res.ok) throw new Error('Could not create ticket');
      const data = await res.json();
      setTicketCreated(data);
      setShowTicketForm(false);
    } catch (e) {
      toast.error('Sorry — failed to create your ticket. Please WhatsApp us instead.');
    } finally {
      setTicketSubmitting(false);
    }
  };

  const resetChat = () => {
    setMessages([WELCOME]);
    setShowTicketForm(false);
    setTicketCreated(null);
    setInput('');
  };

  if (!visible) return null;

  return (
    <div className="fixed top-[84px] lg:top-[92px] right-3 sm:right-6 z-[60] flex flex-col items-end gap-3" data-testid="bharat-ai-chat">
      {open && (
        <div
          className="w-[94vw] max-w-[400px] h-[640px] max-h-[calc(100vh-110px)] rounded-2xl shadow-2xl border border-black/5 bg-white flex flex-col overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-200"
          data-testid="bharat-ai-panel"
        >
          {/* HEADER */}
          <div className="bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] text-white px-5 py-4 relative overflow-hidden">
            <div className="pointer-events-none absolute inset-0 overflow-hidden opacity-60">
              <span className="be-aurora-blob be-aurora-blob--saffron" style={{ opacity: 0.35 }} />
              <span className="be-aurora-blob be-aurora-blob--gold" style={{ opacity: 0.35 }} />
            </div>
            <div className="relative flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-[#D4AF37] text-[#0F172A] flex items-center justify-center shadow-lg shadow-[#D4AF37]/30">
                  <Sparkles className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-semibold leading-tight">Bharat AI</p>
                  <p className="text-[11px] text-white/65 flex items-center gap-1.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#10B981] inline-block" />
                    Online · Replies instantly
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {ttsSupported && (
                  <button
                    onClick={() => {
                      if (autoSpeak) { stopSpeaking(); setAutoSpeak(false); }
                      else setAutoSpeak(true);
                    }}
                    title={autoSpeak ? 'Mute voice replies' : 'Read replies aloud'}
                    className={`h-8 w-8 rounded-lg flex items-center justify-center transition-colors ${autoSpeak ? 'bg-[#D4AF37] text-[#0F172A]' : 'hover:bg-white/10'}`}
                    data-testid="chat-tts-toggle"
                  >
                    {autoSpeak ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                  </button>
                )}
                <button
                  onClick={resetChat}
                  title="New chat"
                  className="h-8 w-8 rounded-lg hover:bg-white/10 flex items-center justify-center transition-colors"
                  data-testid="chat-reset"
                >
                  <RefreshCw className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setOpen(false)}
                  title="Close"
                  className="h-8 w-8 rounded-lg hover:bg-white/10 flex items-center justify-center transition-colors"
                  data-testid="chat-close"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          {/* MESSAGES */}
          <div ref={scrollerRef} className="flex-1 overflow-y-auto bg-[#F9F9F7] px-4 py-4 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap shadow-sm ${
                    m.role === 'user'
                      ? 'bg-[#0F172A] text-white rounded-tr-sm'
                      : 'bg-white text-[#0F172A] border border-black/5 rounded-tl-sm'
                  }`}
                  data-testid={`chat-msg-${m.role}-${i}`}
                >
                  {m.text}
                </div>
              </div>
            ))}

            {sending && (
              <div className="flex justify-start">
                <div className="bg-white border border-black/5 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm flex items-center gap-1.5" data-testid="chat-typing">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#D4AF37] animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="h-1.5 w-1.5 rounded-full bg-[#D4AF37] animate-bounce" style={{ animationDelay: '120ms' }} />
                  <span className="h-1.5 w-1.5 rounded-full bg-[#D4AF37] animate-bounce" style={{ animationDelay: '240ms' }} />
                </div>
              </div>
            )}

            {/* Suggested prompts (only when fresh chat) */}
            {messages.length === 1 && !sending && (
              <div className="pt-2">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2 px-1">Try asking</p>
                <div className="flex flex-col gap-2">
                  {SUGGESTED_PROMPTS.map((p) => (
                    <button
                      key={p}
                      onClick={() => sendMessage(p)}
                      className="text-left text-sm bg-white hover:bg-[#D4AF37]/10 border border-black/5 hover:border-[#D4AF37]/50 text-[#0F172A] px-3 py-2 rounded-xl transition-colors"
                      data-testid="chat-suggested"
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* TICKET FORM */}
            {showTicketForm && !ticketCreated && (
              <div className="bg-white border-2 border-[#D4AF37]/40 rounded-2xl p-4 shadow-sm" data-testid="chat-ticket-form">
                <div className="flex items-center gap-2 mb-3">
                  <Headphones className="h-4 w-4 text-[#D4AF37]" />
                  <p className="font-semibold text-[#0F172A] text-sm">Raise a support ticket</p>
                </div>
                <p className="text-xs text-muted-foreground mb-3">
                  Our team will get back within <span className="font-semibold text-[#0F172A]">24 hours</span>.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    placeholder="Your name *"
                    value={ticketForm.name}
                    onChange={(e) => setTicketForm({ ...ticketForm, name: e.target.value })}
                    className="border border-black/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#D4AF37] col-span-2"
                    data-testid="ticket-name"
                  />
                  <input
                    type="email"
                    placeholder="Email *"
                    value={ticketForm.email}
                    onChange={(e) => setTicketForm({ ...ticketForm, email: e.target.value })}
                    className="border border-black/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#D4AF37]"
                    data-testid="ticket-email"
                  />
                  <input
                    type="tel"
                    placeholder="Phone (optional)"
                    value={ticketForm.phone}
                    onChange={(e) => setTicketForm({ ...ticketForm, phone: e.target.value })}
                    className="border border-black/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#D4AF37]"
                    data-testid="ticket-phone"
                  />
                  <input
                    type="text"
                    placeholder="Subject *"
                    value={ticketForm.subject}
                    onChange={(e) => setTicketForm({ ...ticketForm, subject: e.target.value })}
                    className="border border-black/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#D4AF37] col-span-2"
                    data-testid="ticket-subject"
                  />
                  <textarea
                    placeholder="Describe your question *"
                    rows={3}
                    value={ticketForm.message}
                    onChange={(e) => setTicketForm({ ...ticketForm, message: e.target.value })}
                    className="border border-black/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#D4AF37] col-span-2 resize-none"
                    data-testid="ticket-message"
                  />
                </div>
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={submitTicket}
                    disabled={ticketSubmitting}
                    className="flex-1 bg-[#D4AF37] hover:bg-[#b8932f] disabled:opacity-60 text-[#0F172A] font-semibold text-sm px-4 py-2.5 rounded-lg flex items-center justify-center gap-2 transition-colors"
                    data-testid="ticket-submit"
                  >
                    {ticketSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                    {ticketSubmitting ? 'Submitting…' : 'Submit ticket'}
                  </button>
                  <button
                    onClick={() => setShowTicketForm(false)}
                    className="px-3 py-2 text-sm text-muted-foreground hover:text-[#0F172A] transition-colors"
                  >
                    Not now
                  </button>
                </div>
              </div>
            )}

            {/* TICKET SUCCESS */}
            {ticketCreated && (
              <div className="bg-gradient-to-br from-[#10B981]/10 to-transparent border border-[#10B981]/40 rounded-2xl p-4" data-testid="chat-ticket-success">
                <div className="flex items-start gap-3">
                  <div className="h-9 w-9 rounded-full bg-[#10B981] text-white flex items-center justify-center flex-shrink-0">
                    <Check className="h-5 w-5" strokeWidth={3} />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-[#0F172A] text-sm">Ticket created — we&apos;re on it!</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Reference: <span className="font-mono font-semibold text-[#0F172A]">{ticketCreated.ticket_id}</span>
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      A confirmation has been sent to your email. Our team will respond within 24 hours.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* COMPOSER */}
          <div className="border-t border-black/5 bg-white p-3">
            {listening && (
              <div className="flex items-center justify-between gap-3 mb-2 bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-lg px-3 py-2" data-testid="chat-listening-bar">
                <div className="flex items-center gap-2 text-[#EF4444]">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#EF4444] opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-[#EF4444]" />
                  </span>
                  <span className="text-xs font-semibold">Listening…</span>
                </div>
                <span className="text-xs text-muted-foreground truncate flex-1 italic">
                  {interimText || 'Speak now'}
                </span>
                <button
                  onClick={stopListening}
                  className="text-[10px] text-[#EF4444] hover:underline"
                >
                  Stop
                </button>
              </div>
            )}
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                rows={1}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                placeholder={listening ? 'Listening…' : 'Ask me anything about Bharat Estate…'}
                className="flex-1 bg-[#F9F9F7] border border-black/5 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[#D4AF37] resize-none max-h-24"
                data-testid="chat-input"
              />
              {voiceSupported && (
                <button
                  onClick={toggleMic}
                  disabled={sending}
                  className={`h-10 w-10 rounded-xl flex items-center justify-center transition-all flex-shrink-0 ${
                    listening
                      ? 'bg-[#EF4444] text-white shadow-lg shadow-[#EF4444]/30 scale-105'
                      : 'bg-[#F9F9F7] hover:bg-[#0F172A] hover:text-white text-[#0F172A] border border-black/10'
                  }`}
                  aria-label={listening ? 'Stop listening' : 'Voice input'}
                  title={listening ? 'Stop listening' : 'Voice input'}
                  data-testid="chat-mic"
                >
                  {listening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                </button>
              )}
              <button
                onClick={() => sendMessage()}
                disabled={!input.trim() || sending}
                className="h-10 w-10 rounded-xl bg-[#D4AF37] hover:bg-[#b8932f] disabled:opacity-50 disabled:cursor-not-allowed text-[#0F172A] flex items-center justify-center transition-colors flex-shrink-0"
                aria-label="Send"
                data-testid="chat-send"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-[10px] text-muted-foreground text-center mt-2 flex items-center justify-center gap-2 flex-wrap">
              Powered by Bharat AI{voiceSupported && ' · Tap the mic to speak'} · Or
              <a
                href="https://wa.me/919465024516"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[#25D366] hover:underline"
                data-testid="chat-whatsapp"
              >
                <Phone className="h-3 w-3" /> chat on WhatsApp
              </a>
            </p>
          </div>
        </div>
      )}

    </div>
  );
};

export default BharatAIChat;
